import ReactApexChart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import { RECOMMENDATIONS, type RecommendationValue } from "@ld-scorecard/shared";
import { useTheme } from "../theme";

// Recommendation → color, shared across charts.
export const REC_COLORS: Record<RecommendationValue, string> = {
  strong_hire: "#16a34a",
  hire: "#2563eb",
  lean_no: "#f59e0b",
  no: "#dc2626",
};

const ACCENT = "#6366f1";
const BASELINE = "#94a3b8";

function useChartBase(): { mode: "light" | "dark"; foreColor: string } {
  const { resolved } = useTheme();
  return { mode: resolved, foreColor: resolved === "dark" ? "#cbd5e1" : "#475569" };
}

/** Radar of the six rubric categories (0–5), optionally overlaid with a baseline. */
export function CategoryRadar({
  labels,
  series,
  height = 340,
}: {
  labels: string[];
  series: { name: string; data: number[] }[];
  height?: number;
}) {
  const { mode, foreColor } = useChartBase();
  const options: ApexOptions = {
    chart: { type: "radar", toolbar: { show: false }, foreColor },
    theme: { mode },
    labels,
    colors: [ACCENT, BASELINE],
    stroke: { width: 2 },
    fill: { opacity: 0.15 },
    markers: { size: 3 },
    yaxis: { min: 0, max: 5, tickAmount: 5, labels: { formatter: (v) => v.toFixed(0) } },
    legend: { show: series.length > 1, position: "bottom" },
  };
  return <ReactApexChart type="radar" options={options} series={series} height={height} />;
}

/** Horizontal bar ranking labels by a 0–5 value, each with its own color. */
export function RankingBar({
  items,
  height,
}: {
  items: { label: string; value: number; color: string }[];
  height?: number;
}) {
  const { mode, foreColor } = useChartBase();
  const options: ApexOptions = {
    chart: { type: "bar", toolbar: { show: false }, foreColor },
    theme: { mode },
    plotOptions: { bar: { horizontal: true, borderRadius: 4, distributed: true, barHeight: "62%" } },
    colors: items.map((i) => i.color),
    dataLabels: { enabled: true, formatter: (v) => (typeof v === "number" ? v.toFixed(1) : "") },
    xaxis: { categories: items.map((i) => i.label), min: 0, max: 5 },
    yaxis: { labels: { maxWidth: 220 } },
    legend: { show: false },
    tooltip: { y: { formatter: (v) => `${v?.toFixed?.(1) ?? v} / 5` } },
  };
  return (
    <ReactApexChart
      type="bar"
      options={options}
      series={[{ name: "Weighted overall", data: items.map((i) => i.value) }]}
      height={height ?? Math.max(140, items.length * 46 + 40)}
    />
  );
}

/** Donut of the recommendation tally. */
export function RecommendationDonut({
  tally,
  height = 300,
}: {
  tally: Record<RecommendationValue, number>;
  height?: number;
}) {
  const { mode, foreColor } = useChartBase();
  const present = RECOMMENDATIONS.filter((r) => tally[r.value] > 0);
  const options: ApexOptions = {
    chart: { type: "donut", foreColor },
    theme: { mode },
    labels: present.map((r) => r.label),
    colors: present.map((r) => REC_COLORS[r.value]),
    legend: { position: "bottom" },
    dataLabels: { enabled: true, formatter: (_v, o) => `${o.w.config.series[o.seriesIndex]}` },
    plotOptions: { pie: { donut: { labels: { show: true, total: { show: true, label: "Scorecards" } } } } },
  };
  return (
    <ReactApexChart
      type="donut"
      options={options}
      series={present.map((r) => tally[r.value])}
      height={height}
    />
  );
}

/** Panelist spread per category: min–max range bar with the mean marked. */
export function SpreadBar({
  labels,
  ranges,
  height = 340,
}: {
  labels: string[];
  ranges: { min: number; max: number }[];
  height?: number;
}) {
  const { mode, foreColor } = useChartBase();
  const options: ApexOptions = {
    chart: { type: "rangeBar", toolbar: { show: false }, foreColor },
    theme: { mode },
    plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: "55%" } },
    colors: [ACCENT],
    xaxis: { min: 0, max: 5, tickAmount: 5 },
    yaxis: { labels: { maxWidth: 220 } },
    tooltip: {
      custom: ({ dataPointIndex }) => {
        const r = ranges[dataPointIndex];
        return `<div class="px-2 py-1"><strong>${labels[dataPointIndex]}</strong><br/>min ${r.min} · max ${r.max}</div>`;
      },
    },
  };
  const series = [
    {
      name: "Score range",
      data: labels.map((label, i) => ({ x: label, y: [ranges[i].min, ranges[i].max] })),
    },
  ];
  return <ReactApexChart type="rangeBar" options={options} series={series} height={height} />;
}
